import React from 'react';
import { 
  Box, 
  Button, 
  VStack, 
  Text, 
  Heading, 
  Icon, 
  HStack,
  Badge,
  useColorModeValue
} from '@chakra-ui/react';
import { FaPlay, FaVideo, FaExternalLinkAlt, FaYoutube, FaGraduationCap, FaCheckCircle, FaBell, FaUpload } from 'react-icons/fa';
import { MdMenuBook, MdApproval, MdNotifications } from 'react-icons/md';


const InformativeVideo = () => {
  // Get user role from localStorage
  const user = localStorage.getItem('user');
  const role = user ? JSON.parse(user).role : null;

  // Role-based content configuration
 const roleContent = {
    talukaUser: {
      title: "HOW TO UPLOAD FILE",
      description: "Watch this video for clarification on how to upload files. This tutorial will guide you through the complete file upload process step by step.",
      buttonText: "Watch Video",
      link: "https://drive.google.com/file/d/1_scJ5T2WmyOf4WEARZ0k9xmKDkejLXoM/view?usp=sharing",
      icon: FaUpload,
      colorScheme: "yellow",
      gradientFrom: "yellow.500",
      gradientTo: "orange.500",
      badgeText: "Upload Guide",
      badgeIcon: FaUpload
    },
    schemeAdmin: {
      title: "OIC USER Guide",
      description: "Watch this video for clarification on how to approve, reject, and how to send notifications for missing files. Learn to manage the system effectively with this complete scheme-admin guide.",
      buttonText: "Watch Video",
      link: "https://drive.google.com/file/d/1j0ofb9Ikwl0EDLITZlUvlEL9gSGWWgl2/view?usp=sharing",
      icon: MdApproval,
      colorScheme: "green",
      gradientFrom: "green.500",
      gradientTo: "teal.500",
      badgeText: "Admin Guide",
      badgeIcon: MdNotifications
    }
  };

  // Get content based on user role
  const content = roleContent[role] || '';

  const handleClick = () => {
    window.open(content.link, '_blank', 'noopener,noreferrer');
  };

  // Dynamic colors based on color mode
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.900, gray.800)'
  );
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue(`${content.colorScheme}.100`, `${content.colorScheme}.700`);

  return (
    <Box 
      minH="calc(100vh - 200px)"
      bgGradient={bgGradient}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
      marginTop={'100px'}
    >
      {/* Main Content Card */}
      <Box
        maxW="600px"
        w="full"
        bg={cardBg}
        borderRadius="2xl"
        boxShadow="2xl"
        overflow="hidden"
        position="relative"
        border="1px solid"
        borderColor={borderColor}
      >
        {/* Decorative Header with role-based gradient */}
        <Box
          bgGradient={`linear(to-r, ${content.gradientFrom}, ${content.gradientTo})`}
          h="12px"
          w="full"
        />
        
        {/* Content */}
        <VStack spacing={8} p={10}>
          {/* Icon and Title */}
          <VStack spacing={4}>
            <Box
              bg={`${content.colorScheme}.50`}
              p={4}
              borderRadius="full"
              boxShadow="lg"
            >
              <Icon as={content.icon} w={10} h={10} color={`${content.colorScheme}.500`} />
            </Box>
            
            <Heading 
              size="xl" 
              textAlign="center" 
              bgGradient={`linear(to-r, ${content.gradientFrom}, ${content.gradientTo})`} 
              bgClip="text"
            >
              {content.title}
            </Heading>
            
            <Text fontSize="lg" color={textColor} textAlign="center" maxW="400px">
              {content.description}
            </Text>
          </VStack>

          {/* Role-based Feature Tags */}
          <HStack spacing={4} wrap="wrap" justify="center">
            {role === 'talukaUser' && (
              <>
                <Badge colorScheme="yellow" px={3} py={1} borderRadius="full" fontSize="sm">
                  <HStack spacing={1}>
                    <Icon as={FaUpload} />
                    <Text>File Upload</Text>
                  </HStack>
                </Badge>
                <Badge colorScheme="orange" px={3} py={1} borderRadius="full" fontSize="sm">
                  <HStack spacing={1}>
                    <Icon as={FaPlay} />
                    <Text>Step-by-Step Guide</Text>
                  </HStack>
                </Badge>
              </>
            )}
            
            {role === 'schemeAdmin' && (
              <>
                <Badge colorScheme="green" px={3} py={1} borderRadius="full" fontSize="sm">
                  <HStack spacing={1}>
                    <Icon as={FaCheckCircle} />
                    <Text>Approve/Reject</Text>
                  </HStack>
                </Badge>
                <Badge colorScheme="teal" px={3} py={1} borderRadius="full" fontSize="sm">
                  <HStack spacing={1}>
                    <Icon as={FaBell} />
                    <Text>Notifications</Text>
                  </HStack>
                </Badge>
              </>
            )}

            {!role && (
              <>
                <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
                  <HStack spacing={1}>
                    <Icon as={FaGraduationCap} />
                    <Text>Educational</Text>
                  </HStack>
                </Badge>
                <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="sm">
                  <HStack spacing={1}>
                    <Icon as={FaYoutube} />
                    <Text>Video</Text>
                  </HStack>
                </Badge>
              </>
            )}
          </HStack>

          {/* Main Button with role-based styling */}
          <Button
            size="lg"
            colorScheme={content.colorScheme}
            onClick={handleClick}
            rightIcon={<FaExternalLinkAlt />}
            px={8}
            py={6}
            fontSize="lg"
            borderRadius="full"
            boxShadow="lg"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'xl',
              bg: `${content.colorScheme}.600`,
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            transition="all 0.3s"
            w={{ base: 'full', sm: 'auto' }}
          >
            {content.buttonText}
          </Button>

        
        </VStack>
      </Box>
    </Box>
  );
};

export default InformativeVideo;